import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { type RboConfig, rboConfigField } from '@openx/types/deal';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getRboLabels } from '../../DealForm/utils/dealFormUtils';
import { useRboFields } from '../../hooks/useRboFields';

type Props = {
  rboConfig?: RboConfig;
  title?: string;
};

const prepareRboConfig = (rboConfig?: RboConfig) => {
  if (!rboConfig) {
    return;
  }

  const dailyRequestGoal = rboConfig?.[rboConfigField.HOURLY_TARGET];
  const modelGoal = rboConfig?.[rboConfigField.MODEL_GOAL];

  const updatedConfig = {
    ...rboConfig,
    [rboConfigField.HOURLY_TARGET]: dailyRequestGoal && bigNumberWithCommas(+dailyRequestGoal),
  };

  // Remove Channel for Conversion Deals
  if (modelGoal === 'conversion') {
    delete updatedConfig[rboConfigField.CHANNEL];
  }

  return updatedConfig;
};

export const ResultsByOpenXReadonly = ({ rboConfig, title }: Props) => {
  const { t } = useTranslation();
  const rboFields = useRboFields({ readonly: true });
  const labels = getRboLabels(t);

  const rboConfigReadonly = useMemo(() => prepareRboConfig(rboConfig), [rboConfig]);

  if (!rboConfigReadonly) {
    return;
  }

  return (
    <>
      {title && (
        <Typography
          data-test="custom-settings-fields-readonly-label"
          variant="h3"
          sx={{
            mb: 1,
            mt: 2,
          }}
        >
          {title}
        </Typography>
      )}
      {rboFields
        .filter(({ field }) => !(rboConfigReadonly?.goal === 'conversion' && field === rboConfigField.CHANNEL))
        .map(({ field, options }) => {
          const option = rboConfigReadonly[field] ?? '';
          return <FieldDetails key={field} title={labels[field]} value={options?.[option] ?? option} />;
        })}
    </>
  );
};
